import { useCallback, useEffect, useMemo, useRef } from "react";
import { boundsOf, cameraForBounds, clampZoom, readBlocks, zoomAt } from "@worktools/shared";
import "../styles/board.css";
import type { Session } from "../discord/session";
import type { JoinRequest, LiveBoard, LobbyConnection } from "../sync/lobby";
import { useUi } from "../state/ui";
import { useBoard } from "../sync/BoardContext";
import { useAwarenessStates, useBlockFrames, useLocalAwareness } from "../sync/hooks";
import { Canvas, isTypingTarget } from "./Canvas";
import { useDialogs } from "../ui/dialogs";
import { ContextMenu } from "./ContextMenu";
import { Hud, type Person } from "./Hud";
import { Dock } from "./Dock";
import { Navigator } from "./Navigator";
import { SpotlightBanner } from "./SpotlightBanner";
import { GrantBar } from "./GrantBar";
import { ReviewPanel } from "./ReviewPanel";
import { AskPanel } from "./AskPanel";
import {
  copyBlocks,
  deleteBlocks,
  duplicateBlocks,
  pasteBlocks,
} from "../board/actions";

export function BoardView({
  session,
  liveBoards,
  joinRequests,
  lobby,
  onLeaveBoard,
  onBoardsChanged,
}: {
  session: Session;
  liveBoards: LiveBoard[];
  joinRequests: JoinRequest[];
  lobby: LobbyConnection | null;
  onLeaveBoard: () => void;
  onBoardsChanged: () => Promise<unknown>;
}) {
  const { doc, me, board } = useBoard();
  const viewportRef = useRef<HTMLDivElement>(null);
  const frames = useBlockFrames();
  const remotes = useAwarenessStates();
  const publish = useLocalAwareness();
  const dialogs = useDialogs();

  const camera = useUi((state) => state.camera);
  const setCamera = useUi((state) => state.setCamera);
  const selection = useUi((state) => state.selection);
  const setSelection = useUi((state) => state.setSelection);
  const followingId = useUi((state) => state.followingId);
  const setSpotlightInvite = useUi((state) => state.setSpotlightInvite);
  const lastSpotlight = useRef<number>(Date.now());

  // A different board starts with a clean viewport, selection and banner state.
  useEffect(() => {
    useUi.getState().resetForBoard();
  }, [board.id]);

  const viewportSize = useCallback(() => {
    const rect = viewportRef.current?.getBoundingClientRect();
    return { width: rect?.width ?? window.innerWidth, height: rect?.height ?? window.innerHeight };
  }, []);

  const centerPoint = useCallback(() => {
    const { width, height } = viewportSize();
    return { x: width / 2 / camera.zoom - camera.x, y: height / 2 / camera.zoom - camera.y };
  }, [camera, viewportSize]);

  const zoomToFit = useCallback(
    (ids?: string[]) => {
      const source = ids?.length ? frames.filter((frame) => ids.includes(frame.id)) : frames;
      const bounds = boundsOf(source);
      if (!bounds) return;
      setCamera(cameraForBounds(bounds, viewportSize()));
    },
    [frames, setCamera, viewportSize],
  );

  // Broadcast selection and camera so everybody sees what the others are on.
  useEffect(() => {
    publish({ selection });
  }, [selection, publish]);

  useEffect(() => {
    publish({ camera });
  }, [camera, publish]);

  // Follow mode: mirror another participant's camera.
  useEffect(() => {
    if (!followingId) return;
    const target = remotes.find((state) => state.user.id === followingId);
    if (target?.camera) setCamera(target.camera);
  }, [followingId, remotes, setCamera]);

  // Somebody asked for attention — offer to jump to their viewport.
  useEffect(() => {
    for (const state of remotes) {
      const spotlight = state.spotlight;
      if (!spotlight || spotlight.at <= lastSpotlight.current) continue;
      lastSpotlight.current = spotlight.at;
      setSpotlightInvite({
        fromId: state.user.id,
        fromName: state.user.name,
        color: state.user.color,
        camera: spotlight.camera,
        blockIds: spotlight.blockIds,
        at: spotlight.at,
      });
    }
  }, [remotes, setSpotlightInvite]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const meta = event.metaKey || event.ctrlKey;

      if (meta && event.key.toLowerCase() === "a") {
        event.preventDefault();
        setSelection(frames.map((frame) => frame.id));
        return;
      }
      if (meta && event.key.toLowerCase() === "c" && selection.length) {
        copyBlocks(doc, selection);
        return;
      }
      if (meta && event.key.toLowerCase() === "v") {
        const created = pasteBlocks(doc, centerPoint(), me.id);
        if (created.length) setSelection(created);
        return;
      }
      if (meta && event.key.toLowerCase() === "d" && selection.length) {
        event.preventDefault();
        setSelection(duplicateBlocks(doc, selection, me.id));
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selection.length) {
        event.preventDefault();
        deleteBlocks(doc, selection);
        setSelection([]);
        return;
      }
      if (event.key === "Escape") {
        setSelection([]);
        useUi.getState().closeContextMenu();
        useUi.getState().setFollowing(null);
        return;
      }
      if (event.key === "1" && meta) {
        event.preventDefault();
        zoomToFit();
        return;
      }
      if (event.key === "v") useUi.getState().setTool("select");
      if (event.key === "h") useUi.getState().setTool("pan");
      if (event.key === "p") useUi.getState().setTool("pen");
      if (event.key === "e") useUi.getState().setTool("eraser");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, frames, selection, me.id, centerPoint, setSelection, zoomToFit]);

  const participants = useMemo(() => {
    const byId = new Map<string, Person>();
    byId.set(me.id, { id: me.id, name: me.name, color: me.color, avatarUrl: me.avatarUrl, isMe: true });
    for (const state of remotes) {
      // Several connections of one person collapse into a single avatar.
      if (byId.has(state.user.id)) continue;
      byId.set(state.user.id, {
        ...state.user,
        ownerName: (state.user as { ownerName?: string }).ownerName,
        isMe: false,
      });
    }
    return [...byId.values()];
  }, [me, remotes]);

  const requestSpotlight = () => {
    publish({ spotlight: { at: Date.now(), camera, blockIds: selection } });
    dialogs.toast(
      remotes.length === 0
        ? "Niemand sonst ist gerade im Board"
        : `${remotes.length} ${remotes.length === 1 ? "Teilnehmer wurde" : "Teilnehmer wurden"} auf deinen Ausschnitt gelenkt`,
    );
  };

  return (
    <div className="app">
      <Hud
        participants={participants}
        liveBoards={liveBoards}
        joinRequests={joinRequests}
        lobby={lobby}
        onLeaveBoard={onLeaveBoard}
        onBoardsChanged={onBoardsChanged}
        onSpotlight={requestSpotlight}
        blockCount={readBlocks(doc).length}
      />
      <div className="body">
        <Dock centerPoint={centerPoint} />
        <Canvas viewportRef={viewportRef} />
        <Navigator
          viewportSize={viewportSize}
          onZoom={(factor) => {
            const { width, height } = viewportSize();
            setCamera((current) => zoomAt(current, { x: width / 2, y: height / 2 }, current.zoom * factor));
          }}
          onReset={() => setCamera((current) => ({ ...current, zoom: clampZoom(1) }))}
          onFit={() => zoomToFit()}
        />
        <AskPanel />
        <ReviewPanel />
        <GrantBar />
        <SpotlightBanner />
        <ContextMenu viewportRef={viewportRef} zoomToFit={zoomToFit} />
      </div>
    </div>
  );
}
